/**
 * Table Colors Component
 *
 * A responsive table colors component for the Order Details block.
 * Provides controls for table border colors, header colors, and total row colors.
 *
 * @package RedfoxThankYou
 * @since 1.0.0
 */

import { PanelBody, ResetButton } from '@redfox-thank-you/components';
import { BaseControl, ColorPicker } from '@redfox-thank-you/controls';
import { useSelect } from '@wordpress/data';
import { store as editorStore } from '@wordpress/editor';
import { __ } from '@wordpress/i18n';
import React from 'react';

import type { TableColorsComponentProps, TableColorsValues } from './types';

/**
 * Table Colors Component
 */
export const TableColorsComponent: React.FC< TableColorsComponentProps > = ( {
	attributes,
	setAttributes,
	title = __( 'Table Colors', 'redfox-thank-you' ),
	initialOpen = false,
	className = '',
	showResponsive = true,
} ) => {
	// Get WordPress editor device type for responsive support
	const deviceType = useSelect( ( select ) => {
		const editorSelect = select(
			editorStore,
		) as unknown as import('@wordpress/editor').EditorStore;
		return editorSelect?.getDeviceType?.() ?? 'Desktop';
	}, [] );

	// Map WordPress device types to our breakpoint types
	const deviceTypeToBreakpoint = ( type: string ) => {
		switch ( type ) {
			case 'Tablet':
				return 'tablet';
			case 'Mobile':
				return 'mobile';
			default:
				return 'desktop';
		}
	};

	const activeBreakpoint = deviceTypeToBreakpoint( deviceType as string );

	// Get current values for the active breakpoint
	const getCurrentValues = () => {
		const currentBreakpointValues = attributes[ activeBreakpoint ] || {};
		return {
			borderColor: currentBreakpointValues.borderColor || '',
			headerColor: currentBreakpointValues.headerColor || '',
			totalRowColor: currentBreakpointValues.totalRowColor || '',
		};
	};

	const currentValues = getCurrentValues();

	// Update attribute for current breakpoint
	const updateAttribute = ( key: keyof TableColorsValues, value: string ) => {
		const currentBreakpointValues = attributes[ activeBreakpoint ] || {};

		const updatedAttributes = {
			...attributes,
			[ activeBreakpoint ]: {
				...currentBreakpointValues,
				[ key ]: value,
			},
		};

		setAttributes( updatedAttributes );
	};

	// Reset all table colors
	const resetTableColors = () => {
		setAttributes( {
			desktop: {},
			tablet: {},
			mobile: {},
		} );
	};

	// Check if any table color values exist
	const hasTableColorValues = () => {
		const breakpoints = [ 'desktop', 'tablet', 'mobile' ] as const;
		return breakpoints.some( ( breakpoint ) => {
			const values = attributes[ breakpoint ];
			return (
				values &&
				Object.values( values ).some( ( value ) => value !== '' )
			);
		} );
	};

	return (
		<PanelBody
			title={ title }
			initialOpen={ initialOpen }
			className={ className }
			showResponsive={ showResponsive }
			responsiveProps={ {
				showLabels: false,
				className: 'redfox-thank-you-table-colors-responsive',
			} }
			headerActions={
				hasTableColorValues() && (
					<ResetButton
						onReset={ resetTableColors }
						show={ hasTableColorValues() }
						label={ __( 'Reset Background', 'redfox-thank-you' ) }
						className="redfox-thank-you-background-control__reset"
					/>
				)
			}
		>
			<BaseControl
				id="table-border-color"
				label={ __( 'Border Color', 'redfox-thank-you' ) }
				help={ __(
					'Choose a color for table borders.',
					'redfox-thank-you',
				) }
				inline={ true }
				showResetButton={ true }
				onReset={ () => updateAttribute( 'borderColor', '' ) }
				hasValue={ currentValues.borderColor !== '' }
			>
				<ColorPicker
					value={ currentValues.borderColor }
					onChange={ ( color ) =>
						updateAttribute( 'borderColor', color || '' )
					}
				/>
			</BaseControl>

			<BaseControl
				id="table-header-color"
				label={ __( 'Header Text Color', 'redfox-thank-you' ) }
				help={ __(
					'Choose a color for table header text.',
					'redfox-thank-you',
				) }
				inline={ true }
				showResetButton={ true }
				onReset={ () => updateAttribute( 'headerColor', '' ) }
				hasValue={ currentValues.headerColor !== '' }
			>
				<ColorPicker
					value={ currentValues.headerColor }
					onChange={ ( color ) =>
						updateAttribute( 'headerColor', color || '' )
					}
				/>
			</BaseControl>

			<BaseControl
				id="total-row-color"
				label={ __( 'Total Row Color', 'redfox-thank-you' ) }
				help={ __(
					'Choose a color for the total row text.',
					'redfox-thank-you',
				) }
				inline={ true }
				showResetButton={ true }
				onReset={ () => updateAttribute( 'totalRowColor', '' ) }
				hasValue={ currentValues.totalRowColor !== '' }
			>
				<ColorPicker
					value={ currentValues.totalRowColor }
					onChange={ ( color ) =>
						updateAttribute( 'totalRowColor', color || '' )
					}
				/>
			</BaseControl>
		</PanelBody>
	);
};
